import { Stack, useTheme } from '@mui/material'
import {
    assembleAssetTitle,
    Button,
    Image,
    isoDateStringToLocalFormat,
    joinDefinedStrings,
    MonetaryValue,
    Percentage,
    tokens,
    TrendIcon,
    Typography,
    ValueLabel,
} from 'ComponentLibrary'
import { AssetLocation } from 'ClientPortal/src/pages/MarketData/types'
import { XIcon } from '@phosphor-icons/react'
import { useCurrentResolvedLocale } from 'ClientPortal/src/i18n/currentLocale'
import { useTranslation } from 'react-i18next'

type MarketDataSidebarSelectionProps = {
    selectedLocation: AssetLocation
    setSelectedLocation: (location?: AssetLocation) => void
}

export const MarketDataSidebarSelection = ({ selectedLocation, setSelectedLocation }: MarketDataSidebarSelectionProps) => {
    const theme = useTheme()
    const { t: tCommon } = useTranslation('terminology', { keyPrefix: 'common' })
    const { t } = useTranslation('pages', { keyPrefix: 'marketData.sidebarSelection' })
    const { t: tSidebar } = useTranslation('pages', { keyPrefix: 'marketData.sidebar' })
    const locale = useCurrentResolvedLocale()

    return (
        <Stack direction="column" gap={theme.spacing(2)}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Image
                    sx={{ borderRadius: '8px' }}
                    height="54"
                    width="82"
                    src={(selectedLocation.uuid && selectedLocation.title_image) ?? '/asset-placeholder.svg'}
                    alt={tCommon('assetImageAlt')}
                />
                <Button variant="secondary" startIcon={<XIcon size="20px" />} onClick={() => setSelectedLocation(undefined)} />
            </Stack>
            <Stack direction="row" gap={theme.spacing(2)} width="100%" color={theme.palette.primary1.blue100}>
                <Stack direction="column" gap={theme.spacing(2)} width="100%">
                    <Stack direction="row" justifyContent="space-between" alignItems="center" width="100%" gap={tokens.SizingSpacing100}>
                        <Typography variant="headline3">
                            {selectedLocation.uuid
                                ? assembleAssetTitle({
                                      ...selectedLocation.address,
                                      customer_asset_reference: selectedLocation.customer_asset_reference,
                                  })
                                : tSidebar('customLocation')}
                        </Typography>
                        {selectedLocation.uuid && selectedLocation.valuation.market_value.trend !== undefined && (
                            <TrendIcon trend={selectedLocation.valuation.market_value.trend} variant="label" locale={locale} />
                        )}
                    </Stack>
                    {selectedLocation.uuid ? (
                        <Stack direction="column" gap={theme.spacing(1)}>
                            {selectedLocation.valuation.market_value.value !== undefined && (
                                <ValueLabel
                                    label={t('marketValue')}
                                    value={MonetaryValue.fromMonetaryValueDataObject(selectedLocation.valuation.market_value.value).format({
                                        locale,
                                    })}
                                />
                            )}
                            {selectedLocation.valuation.latest_valuation_date !== undefined && (
                                <ValueLabel
                                    label={tCommon('valuationDate')}
                                    value={
                                        isoDateStringToLocalFormat(
                                            selectedLocation.valuation.latest_valuation_date,
                                            false,
                                            locale
                                        ) as string
                                    }
                                />
                            )}
                            {selectedLocation.rent?.summary.total?.gross_rental_income.total !== undefined && (
                                <ValueLabel
                                    label={t('marketRent')}
                                    value={MonetaryValue.fromMonetaryValueDataObject(
                                        selectedLocation.rent.summary.total.gross_rental_income.total
                                    ).format({ locale })}
                                />
                            )}
                            {selectedLocation.rent?.summary.total?.management_cost.total !== undefined && (
                                <ValueLabel
                                    label={t('managementCosts')}
                                    value={MonetaryValue.fromMonetaryValueDataObject(
                                        selectedLocation.rent.summary.total.management_cost.total
                                    ).format({ locale })}
                                />
                            )}
                            {selectedLocation.valuation.gross_yield !== undefined && (
                                <ValueLabel
                                    label={t('grossYield')}
                                    value={new Percentage(selectedLocation.valuation.gross_yield).format({ locale })}
                                />
                            )}
                        </Stack>
                    ) : (
                        <Stack direction="column" gap={theme.spacing(1)}>
                            <Typography variant="bodyTextMedium" color={theme.palette.customtext.black100}>
                                {joinDefinedStrings([selectedLocation.address.street, selectedLocation.address.house_number])}
                            </Typography>
                            <Typography variant="bodyTextMedium" color={theme.palette.customtext.black100}>
                                {joinDefinedStrings([selectedLocation.address.zip, selectedLocation.address.city])}
                            </Typography>
                        </Stack>
                    )}
                </Stack>
            </Stack>
        </Stack>
    )
}
